import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/components/ui/use-toast";
import {
  careKeys,
  useCareReviewCount,
  useCareReviews,
  useMarkCareReviewSeen,
  useUndoCareReview,
  type CareReviewListStatus,
  type CareReviewTodo,
} from "@/lib/api/care";
import { useCurrentUser, useUsers } from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import {
  CARE_REVIEW_PAGE_SIZE,
  defaultSort,
  menteeListLabel,
  menteeMatchesSearch,
  type CareReviewSort,
  type CareReviewSortColumn,
} from "@/lib/care-reviews/review-list";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Check, ClipboardList, Loader2, Undo2 } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { CareReviewDetail } from "./care-review-detail";
import {
  CareReviewClosedFilters,
  CareReviewTable,
  CareReviewToolbar,
} from "./care-review-list";

export function CareReviewsPage() {
  const ability = useAbility();
  const unrestricted = ability.can("manage", "all");
  const hidePhiPii = useDevOverridesStore((state) => state.hidePhiPii);
  const phiHidden = hidePhiPii === true;
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const { toast } = useToast();
  const undo = useUndoCareReview();
  const { mutate: markSeen } = useMarkCareReviewSeen();
  const marked = useRef(new Set<string>());
  const now = DateTime.now();

  const [status, setStatus] = useState<CareReviewListStatus>("open");
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 500);
  const [month, setMonth] = useState(DateTime.utc().toFormat("yyyy-MM"));
  const [supervisorId, setSupervisorId] = useState("");
  const [sort, setSort] = useState<CareReviewSort>(defaultSort("open"));
  const [visibleCount, setVisibleCount] = useState(CARE_REVIEW_PAGE_SIZE);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const counts = useCareReviewCount(true);
  const closedMonth =
    unrestricted && /^\d{4}-\d{2}$/.test(month) ? month : undefined;
  const supervisorFilter =
    unrestricted && status === "closed" && supervisorId
      ? supervisorId
      : undefined;
  const reviewsQuery = useCareReviews(
    {
      status,
      month: status === "closed" ? closedMonth : undefined,
      supervisorId: supervisorFilter,
      sort: sort.column,
      sort_direction: sort.direction,
    },
  );

  const supervisorsQuery = useUsers({
    page: 1,
    perPage: 1000,
    assignedRole: "supervisor",
    status: "active",
    enabled: unrestricted,
  });
  const supervisorOptions = (supervisorsQuery.data?.users ?? [])
    .map((user) => ({ id: user.id, name: user.name ?? "Supervisor" }))
    .sort((left, right) => left.name.localeCompare(right.name));

  const reviews = useMemo(() => {
    const data = reviewsQuery.data ?? [];
    if (!phiHidden || sort.column !== "mentee") return data;
    const direction = sort.direction === "asc" ? 1 : -1;
    return [...data].sort(
      (left, right) =>
        menteeListLabel(left.mentee, true).localeCompare(
          menteeListLabel(right.mentee, true),
        ) * direction,
    );
  }, [phiHidden, reviewsQuery.data, sort.column, sort.direction]);
  const filtered = reviews.filter((review) => {
    return menteeMatchesSearch(review.mentee, debouncedQuery, phiHidden);
  });
  const selectedIndex = filtered.findIndex((review) => review.id === selectedId);
  const visible = filtered.slice(
    0,
    Math.max(visibleCount, selectedIndex + 1),
  );
  const selected =
    filtered.find((review) => review.id === selectedId) ?? filtered[0] ?? null;

  useEffect(() => {
    setVisibleCount(CARE_REVIEW_PAGE_SIZE);
  }, [debouncedQuery]);

  useEffect(() => {
    const id = selected?.id;
    const seen = marked.current;
    return () => {
      if (id) seen.delete(id);
    };
  }, [selected?.id]);

  useEffect(() => {
    if (!selected || selected.seen_at || marked.current.has(selected.id))
      return;
    marked.current.add(selected.id);
    markSeen(selected.id);
  }, [markSeen, selected]);

  const columns = listColumns(unrestricted, status);
  const listWidth = unrestricted ? "w-[560px]" : "w-[528px]";
  const listEmpty =
    !reviewsQuery.isPending && !reviewsQuery.isError && visible.length === 0;

  const onStatusChange = (next: CareReviewListStatus) => {
    if (next === status) return;
    setStatus(next);
    setSort(defaultSort(next));
    setVisibleCount(CARE_REVIEW_PAGE_SIZE);
    setSelectedId(null);
    trackEvent(PostHogEvents.CARE_REVIEWS_TOGGLE_ENTRIES, {
      admin_id: currentUser?.id,
      toggle_type: next,
    });
  };

  const onSort = (column: CareReviewSortColumn) => {
    setSort((current) =>
      current.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : {
            column,
            direction:
              column === "mentee" || column === "cycle" ? "asc" : "desc",
          },
    );
    setVisibleCount(CARE_REVIEW_PAGE_SIZE);
  };

  const onSelect = (review: CareReviewTodo) => {
    setSelectedId(review.id);
  };

  const onSubmitted = () => {
    if (!selected) return;
    const submittedId = selected.id;
    const index = filtered.findIndex((review) => review.id === submittedId);
    const next = filtered[index + 1] ?? filtered[index - 1] ?? null;
    const menteeId = selected.mentee.id;
    const name = menteeListLabel(selected.mentee, phiHidden, "this teen");
    queryClient.setQueriesData<CareReviewTodo[]>(
      { queryKey: ["care", "reviews"] },
      (current) => current?.filter((review) => review.id !== submittedId),
    );
    setSelectedId(next?.id ?? null);
    queryClient.invalidateQueries({ queryKey: ["care", "reviews"] });
    queryClient.invalidateQueries({ queryKey: careKeys.reviewCount });
    const submitted = toast({
      duration: 5000,
      position: "bottom-right",
      className: CARE_REVIEW_TOAST_CLASS,
      description: (
        <span className="flex items-center gap-3">
          <SubmittedToastIcon />
          <span className="grid gap-0.5">
            <span className="font-body text-sm font-bold leading-5 text-white">
              Care review submitted
            </span>
            <span className="font-body text-xs font-normal leading-4 text-[#b5b0a6]">
              Added to{" "}
              <span className="text-white underline">
                {name}&apos;s care history
              </span>
            </span>
          </span>
        </span>
      ),
      action: (
        <ToastAction
          altText="Undo submission"
          className="h-8 rounded-full border-[#8a8275] bg-transparent px-4 font-body text-sm font-bold text-white hover:bg-white/10"
          onClick={() => {
            trackEvent(PostHogEvents.CARE_REVIEWS_SUBMISSION_UNDO, {
              admin_id: currentUser?.id,
              teen_id: menteeId,
            });
            undo.mutate(menteeId, {
              onSuccess: () => {
                setStatus("open");
                setSelectedId(selected.id);
                submitted.dismiss();
                toast({
                  duration: 3000,
                  position: "bottom-right",
                  className: CARE_REVIEW_TOAST_CLASS,
                  description: (
                    <span className="flex items-center gap-3 font-body text-sm font-bold leading-5 text-white">
                      <UndoneToastIcon />
                      Submission undone
                    </span>
                  ),
                });
              },
              onError: (error) => {
                const message = isAxiosError(error)
                  ? error.response?.data?.message
                  : undefined;
                toast({
                  variant: "destructive",
                  title:
                    typeof message === "string"
                      ? message
                      : "Could not undo this submission",
                });
              },
            });
          }}
        >
          Undo
        </ToastAction>
      ),
    });
  };

  const toolbar = (
    <CareReviewToolbar
      query={query}
      onQueryChange={setQuery}
      status={status}
      counts={counts}
      onStatusChange={onStatusChange}
      filters={
        unrestricted && status === "closed" ? (
          <CareReviewClosedFilters
            month={month}
            onMonthChange={(value) => {
              setMonth(value);
              setSupervisorId("");
              setVisibleCount(CARE_REVIEW_PAGE_SIZE);
            }}
            supervisorId={supervisorId}
            onSupervisorChange={(value) => {
              setSupervisorId(value);
              setVisibleCount(CARE_REVIEW_PAGE_SIZE);
            }}
            supervisors={supervisorOptions}
          />
        ) : null
      }
    />
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#f6f4ef] p-4">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden rounded-[28px] bg-[#f3efe6] px-10 py-10">
        <div className="shrink-0">
          <h1 className="font-heading text-[32px] font-bold leading-10 text-[#0e0d0c]">
            Care Reviews
          </h1>
          <p className="font-body text-sm leading-5 text-[#58534b]">
            {now.toFormat("cccc LLLL d, yyyy")}
          </p>
        </div>
        <div
          className={
            listEmpty
              ? "flex min-h-0 flex-1 flex-col gap-4"
              : "flex min-h-0 flex-1 items-stretch gap-[18px]"
          }
        >
          <div
            className={
              listEmpty
                ? `${listWidth} shrink-0`
                : `flex min-h-0 shrink-0 flex-col gap-4 ${listWidth}`
            }
          >
            {toolbar}
            {listEmpty ? null : reviewsQuery.isPending ? (
              <div
                className="flex flex-1 items-center justify-center"
                role="status"
                aria-label="Loading reviews"
              >
                <Loader2 className="size-6 animate-spin text-[#a0998d]" />
              </div>
            ) : reviewsQuery.isError ? (
              <p className="py-16 text-center font-body text-sm text-states-error">
                Unable to load care reviews.
              </p>
            ) : (
              <>
                <CareReviewTable
                  className="min-h-0 shrink"
                  reviews={visible}
                  columns={columns}
                  sort={sort}
                  onSort={onSort}
                  selectedId={selected?.id ?? null}
                  onSelect={onSelect}
                  showMarker={unrestricted || status === "open"}
                  showOpenDays={unrestricted}
                  now={now}
                />
                {filtered.length > visible.length ? (
                  <button
                    type="button"
                    onClick={() =>
                      setVisibleCount((count) => count + CARE_REVIEW_PAGE_SIZE)
                    }
                    className="h-12 shrink-0 rounded-full bg-[#e5e0d2] font-body text-sm font-bold text-[#0e0d0c]"
                  >
                    Show 15 more
                  </button>
                ) : null}
              </>
            )}
          </div>
          {listEmpty ? (
            <EmptyReviews searching={debouncedQuery.trim().length > 0} />
          ) : null}
          {selected ? (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <CareReviewDetail
                key={selected.id}
                reviewId={selected.id}
                submitted={selected.status === "submitted"}
                menteeId={selected.mentee.id}
                menteeName={menteeListLabel(selected.mentee, phiHidden)}
                adminId={currentUser?.id}
                onSubmitted={onSubmitted}
                onDraftSaved={() =>
                  queryClient.invalidateQueries({
                    queryKey: ["care", "reviews"],
                  })
                }
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const CARE_REVIEW_TOAST_CLASS =
  "items-center gap-3 space-x-0 rounded-2xl border-0 bg-[#272421] p-3 pl-4 text-white shadow-[0_8px_24px_rgba(0,0,0,0.28)] [&>[toast-close]]:static [&>[toast-close]]:p-1 [&>[toast-close]]:text-[#c8c3ba] [&>[toast-close]]:opacity-100 [&>[toast-close]]:hover:text-white";

function SubmittedToastIcon() {
  return (
    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#2f9e44]">
      <Check className="size-4 text-white" strokeWidth={3} aria-hidden />
    </span>
  );
}

function UndoneToastIcon() {
  return <Undo2 className="size-5 shrink-0 text-[#e4dfd6]" aria-hidden />;
}

function EmptyReviews({ searching }: { searching: boolean }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1 text-center">
      <ClipboardList className="size-6 text-[#716a60]" aria-hidden />
      <p className="font-body text-base font-bold leading-5 text-[#0e0d0c]">
        {searching ? "No matching reviews" : "Nothing to review"}
      </p>
      <p className="font-body text-sm leading-5 text-[#716a60]">
        {searching ? (
          "Try a different name"
        ) : (
          <>
            When care reviews are requested,
            <br />
            you&apos;ll see them listed here
          </>
        )}
      </p>
    </div>
  );
}

function listColumns(unrestricted: boolean, status: CareReviewListStatus) {
  const columns: { id: CareReviewSortColumn; label: string }[] = [
    { id: "mentee", label: "Mentee" },
  ];
  if (!unrestricted) columns.push({ id: "cycle", label: "Cycle" });
  columns.push(
    status === "open"
      ? { id: "age", label: "Review Age" }
      : { id: "submitted", label: "Review Date" },
  );
  if (unrestricted) columns.push({ id: "assigned", label: "Assigned to" });
  return columns;
}
